import { Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppRoutes } from '@app-routes';
import { EExpressWaybillPaymentStatus, Invoice } from '@models/invoice/invoice.model';
import { IApiError } from '@models/iapi';
import { PaymentCostsInfo } from '@models/acquiring/payment-costs-info';
import {
  AlertToastService,
  AnalyticEventName,
  AnalyticsService,
  AuthService,
  ClaimService,
  GoogleAnalyticService,
  InternationalInvApiService,
  BreakpointService,
} from '@services';
import { checkCanEdit, formatCost, handleFirstDayStorage, handleReturnCargoDate } from '@shared/utils/invoice.util';
import { RedirectInvoicesFormComponent } from '@shared/components/redirect-invoices-form/redirect-invoices-form.component';
import { RedirectSuccessPopupComponent } from '@shared/components/redirect-invoices-form/components/redirect-success-popup/redirect-success-popup.component';
import { RedirectSuccessPopupData } from '@shared/components/redirect-invoices-form/components/redirect-success-popup/redirect-success-popup.interface';
import { todayEqualOrGreaterThan } from '@shared/utils/date.util';
import { AllowedStatusCodes } from '@shared/constants/status-codes.const';
import { PayInvoiceType } from '@shared/other/payment-strategy';
import { ChangeEwSuccessPopupComponent } from '@shared/components/change-ew/change-ew-success-popup/change-ew-success-popup.component';
import { ShowInvoiceTrackedComponent } from '@shared/components/show-invoice-tracked/show-invoice-tracked.component';
import { payerNameTranslationsLong, payerNameTranslationsShort, paymentMethodTranslations } from '@shared/components/show-invoice/show-invoice.constants';
import { FullscreenMobileModalConfig } from '@shared/ui/ui.constants';
import { BannerType } from '@shared/components/simple-banner/simple-banner.constants';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { isNumber } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import {
  BehaviorSubject,
  catchError,
  delayWhen,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  timer,
} from 'rxjs';
import {
  MasterpassPaymentComponent,
} from '../../../components/dashboard/acquiring/components/masterpass-payment/masterpass-payment.component';
import { BusinessSettingsService } from '../../../components/dashboard/settings/services/business-settings.service';
import { InvoiceServiceRequest } from '../../../models/invoice/invoice-service-request.model';
import { FirebaseService } from '../../../services/firebase/firebase.service';
import { InvoiceApiService } from '../../../services/invoices/invoice-api.service';
import { InvoiceService } from '../../../services/invoices/invoice.service';
import { MasterpassService } from '../../../services/masterpass.service';
import { TrackDeliveryService } from '../../../services/track-delivery.service';
import { BackInvoicesFormComponent, BackSuccessPopupComponent, BackSuccessPopupData } from '../back-invoices-form';
import { ChangeCustomerReturnComponent } from '../change-customer-return/change-customer-return.component';
import { ChangeEwSuccessPopupData } from '../change-ew/change-ew-success-popup/change-ew-success-popup.interface';
import { ChangeEwComponent } from '../change-ew/change-ew.component';
import { LightReturnService } from '../light-return-modal/light-return.service';
import { ShowInvoiceService } from './show-invoice.service';
import { InvoiceStateStatus } from '../../../services/invoices/invoice-states.config';

const componentName = 'np-show-invoice';

export type DialogComponentType =
  | RedirectInvoicesFormComponent
  | ShowInvoiceComponent;

@Component({
  selector: componentName,
  templateUrl: './show-invoice.component.html',
  styleUrls: [
    './show-invoice.component.scss',
    '../../../../styles/invoice-show.scss',
  ],
})
export class ShowInvoiceComponent extends Unsubscriber implements OnInit, OnDestroy {
  @HostBinding('class')
  @Input() classes = 'modal-flex';
  @Input() type: PayInvoiceType;
  @Input() contactPhone: string;
  @Input() recipientCounterpartyType: string;
  @Input() backMoney: number;
  @Input() paymentControl: number;
  @Input() isInvoiceFromSearch = false;
  invoice: Invoice | any;
  getStatusDocumentsInvoice: Invoice;
  newInvoice: Invoice;
  formattedInvoiceNumber: string;
  pending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pending$ = this.pending.asObservable();
  loading$: Observable<any> = merge(this.lightReturnService.pending$, this.pending$);
  isShowFull = false;
  redirectRequest: InvoiceServiceRequest;
  returnRequest: InvoiceServiceRequest;
  trackingDocuments = [];
  itemsPerPage = 100;
  isPossibilityDuplicate: boolean;
  closed: EventEmitter<{ success: boolean }> = new EventEmitter<{ success: boolean }>();
  shouldReload: boolean = false;
  paymentCostsInfo: PaymentCostsInfo;
  styleProgress = { 'background-size': '0' };
  readonly payerNameTranslationsLong = payerNameTranslationsLong;
  readonly payerNameTranslationsShort = payerNameTranslationsShort;
  readonly paymentMethodTranslations = paymentMethodTranslations;
  printing70x100 = false;
  printingm100x100 = false;
  currentStateIsArchived = false;
  corporate = false;
  paymentSum: string;
  paymentPopupOpened = false;
  allowManageButton: boolean;
  checkedTerms: boolean;
  private _newInvoicePending = new BehaviorSubject(false);
  newInvoicePending$ = this._newInvoicePending.asObservable();

  isMobile$ = this.breakpointService.isMobile$;
  isDesktop$ = this.breakpointService.isDesktop$;

  set newInvoicePending(value: boolean) {
    this._newInvoicePending.next(value);
  }

  get hideIsPaid(): boolean {
    return this.inputInvoice?.InternationalDeliveryType
      && this.inputInvoice.InternationalDeliveryType === 'Export'
      && this.inputInvoice.PayerType === 'Recipient';
  }

  get showAdditionalServices(): boolean {
    if (!this.inputInvoice) {
      return false;
    }

    return (!!this.inputInvoice.backMoney && !!this.inputInvoice.backDeliveryPayer)
      || !!this.inputInvoice.paymentControl
      || (this.inputInvoice.RedirectingReturnIndication && !this.redirectRequest && !this.returnRequest);
  }

  get invoiceStateInformerDate(): string {
    if(!this.invoice?.state?.name) {
      return '';
    }

    switch (this.invoice?.state?.name) {
      case InvoiceStateStatus.ArrivedAtBranch:
        return this.invoice.actualDeliveryDate || this.invoice.ArrivalDateTime;
      case InvoiceStateStatus.ShipmentReceived:
        return this.invoice.receivingTime;
      default:
        return '';
    }
  }

  commissionPayer: string;
  lightReturnPending$ = this.lightReturnService.pending$;
  protected readonly checkCanEdit = checkCanEdit;
  protected readonly ExpressWaybillPaymentStatus = EExpressWaybillPaymentStatus;
  protected readonly BannerType = BannerType;

  constructor(
    private router: Router,
    private invoiceService: InvoiceService,
    private internationalInvApiService: InternationalInvApiService,
    private invoicesService: InvoiceApiService,
    private alertToastService: AlertToastService,
    public dialogRef: MatDialogRef<ShowInvoiceComponent>,
    public dialog: MatDialog,
    public firebaseService: FirebaseService,
    protected storage: LocalStorageService,
    public authService: AuthService,
    protected googleAnalyticsService: GoogleAnalyticService,
    protected masterpassService: MasterpassService,
    public businessSettingsService: BusinessSettingsService,
    private trackDeliveryService: TrackDeliveryService,
    private lightReturnService: LightReturnService,
    private cs: ClaimService,
    private analyticsService: AnalyticsService,
    private sis: ShowInvoiceService,
    private breakpointService: BreakpointService,
  ) {
    super();
  }

  private _inputInvoice: Invoice;

  get inputInvoice(): any {
    return this._inputInvoice;
  }

  @Input() set inputInvoice(invoice) {
    this._inputInvoice = invoice;
    this.setCommissionPayer(invoice);
  };

  get user() {
    return this.authService.user;
  }

  setCommissionPayer(invoice: Invoice): void {
    if (!invoice) {
      return;
    }

    this.commissionPayer = payerNameTranslationsLong[invoice.backDeliveryPayer] || '';
  }

  ngOnInit(): void {
    if (this.inputInvoice) {
      this.initInvoice();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  paymentCalc(): void {
    if (this.inputInvoice.backMoney) {
      this.paymentSum = (this.inputInvoice.backMoney * 0.02 + 20).toFixed(2);
    }
  }

  getAnnouncedPrice(invoice: Invoice): string {
    return formatCost(invoice.Cost, invoice.InvoiceCurrencyCode, invoice.isInternational);
  }

  getDeliveryPrice(invoice: Invoice): string {
    const price = invoice?.deliveryPrice + invoice?.StoragePrice;

    return formatCost(price, invoice?.DocumentCostCurrencyCode, invoice?.isInternational);
  }

  switchShowFull(): void {
    this.isShowFull = !this.isShowFull;

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.switchShowFull`,
      {
        eventLabel: `Повна інформація`,
        eventCategory: `${componentName}`,
        eventAction: this.isShowFull ? 'open' : 'close',
      },
    );
  }

  redirectInvoice(invoice: Invoice): void {
    const dialogRef = this.dialog.open(RedirectInvoicesFormComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          panelClass: 'no-padding',
        }),
    });

    const typeOfDocument = (invoice.TypeOfDocument === 'Incoming' || this.type === 'in') ? 'in' : 'out';

    dialogRef.componentInstance.type = typeOfDocument;
    dialogRef.componentInstance.inputInvoice = invoice;

    dialogRef.afterClosed()
      .pipe(switchMap((value) => this.handleRedirectPopupClose(value, typeOfDocument)))
      .subscribe(data => {
        if (data?.success) {
          this.dialogRef.close({ success: true });
        }
      });

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.redirectInvoice`,
      {
        eventLabel: `Заявка на переадресацію`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );
    const keys$ = dialogRef
      .keydownEvents()
      .pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();
    this.subscriptions = merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.sis.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  copy(invoice: any): void {
    this.invoicesService
      .getForRef(invoice.docNumber)
      .pipe(map((response) => response.data.data))
      .subscribe((data) => {
        data.map((i) => {
          this.router.navigate([AppRoutes.invoice_copy, i.Ref]);
          this.closePopups();
        });
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.copy`, {
      eventLabel: `Дублювати накладну`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
  }

  copyInternational(invoice: any): void {
    this.setPending(true);

    this.internationalInvApiService.getInvoice(invoice.InternationalExpressWaybill)
      .pipe(finalize(() => this.setPending(false)),
        catchError(() => {
          this.alertToastService.pushError('Не вдалося завантажити дані. Будь ласка, створіть відправлення самостійно');
          return EMPTY;
        }),
      )
      .subscribe((invoice) => {
        if (invoice?.Ref) {
          this.closePopups();
          this.router.navigate([AppRoutes.invoice_copy_international, invoice.Ref]);
        } else {
          this.alertToastService.pushError('Не вдалося завантажити дані. Будь ласка, створіть відправлення самостійно');
        }
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.copy_international`, {
      eventLabel: `Дублювати міжнародну накладну`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
  }

  close(): void {
    this.sis.closeParams = { success: this.shouldReload };
    this.dialogRef.close();
  }

  returnInvoice(invoice: Invoice): void {
    const dialogRef = this.dialog.open(BackInvoicesFormComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          panelClass: 'no-padding',
        }),
    });
    const typeOfDocument = (invoice.TypeOfDocument === 'Incoming' || this.type === 'in') ? 'in' : 'out';
    dialogRef.componentInstance.type = typeOfDocument;
    dialogRef.componentInstance.invoice = invoice;
    dialogRef.keydownEvents().pipe(take(1), filter((e) => e.key === 'Escape')).subscribe(data => {
      dialogRef.close();
    });
    dialogRef.afterClosed().pipe(switchMap((value) => this.handleReturnPopupClose(value, invoice, typeOfDocument)))
      .subscribe(data => {
        if (data?.success) {
          this.dialogRef.close({ success: true });
        }
      });

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.returnInvoice`,
      {
        eventLabel: `Заявка на повернення`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );
    const keys$ = dialogRef
      .keydownEvents()
      .pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();
    merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.sis.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  print(invoices: Invoice[]): void {
    if (!invoices.length) {
      return;
    }

    const invoicesDocNumbers = invoices.map((el: Invoice) => el.docNumber);
    this.googleAnalyticsService.track('analitics', `${componentName}.print`, {
      eventLabel: `Друк експрес-накладної, 2 копiї`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });

    this.invoiceService
      .printCopies(invoicesDocNumbers, 2)
      .pipe(finalize(() => this.sis.shouldReload = this.shouldReload = true))
      .subscribe(
        () => {
          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.print.success`,
            {
              eventLabel: `Друк експрес-накладної, 2 копiї`,
              eventCategory: `${componentName}`,
              eventAction: 'print.success',
            },
          );
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.print.error`,
            {
              eventLabel: `Друк експрес-накладної, 2 копiї: ${
                errors[0] && errors[0].errorCode
              }`,
              eventCategory: `${componentName}`,
              eventAction: 'print.error',
            },
          );
        },
      );
  }

  printOneCopy(invoices: Invoice[]): void {
    if (!invoices.length) {
      return;
    }

    const invoicesDocNumbers = invoices.map((el: Invoice) => el.docNumber);

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.printOneCopy`,
      {
        eventLabel: `Друк експресс-накладної, 1 копія`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );

    this.invoiceService
      .printCopies(invoicesDocNumbers)
      .pipe(finalize(() => this.sis.shouldReload = this.shouldReload = true))
      .subscribe(
        () => {
          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.printOneCopy.success`,
            {
              eventLabel: `Друк експресс-накладної, 1 копія`,
              eventCategory: `${componentName}`,
              eventAction: 'printOneCopy.success',
            },
          );
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.printOneCopy.error`,
            {
              eventLabel: `Друк експресс-накладної, 1 копія: ${
                errors[0] && errors[0].errorCode
              }`,
              eventCategory: `${componentName}`,
              eventAction: 'printOneCopy.error',
            },
          );
        },
      );
  }

  changeEw(invoice: Invoice): void {
    if (!invoice) {
      console.warn('Invoice is undefined');
      return;
    }

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.changeEw`,
      {
        eventLabel: `Заявка на зміну даних`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );

    const dialogRef = this.dialog.open(ChangeEwComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          panelClass: 'no-padding',
          disableClose: true,
        }),
    });
    dialogRef.componentInstance.type = 'out';
    dialogRef.componentInstance.invoice = invoice;
    dialogRef.keydownEvents().pipe(take(1), filter((e) => e.key === 'Escape')).subscribe(data => {
      dialogRef.close();
    });
    dialogRef.afterClosed()
      .pipe(take(1), switchMap((value) => this.handleChangeEwPopupClose(value)))
      .subscribe(data => {
        if (data?.success) {
          this.sis.closeParams = { success: true };
          this.dialogRef.close();
        }
        dialogRef.close();
      });
  }

  changeCustomerReturn(invoice: Invoice): void {
    if (!invoice) {
      console.warn('Invoice is undefined');
      return;
    }

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.changeCustomerReturn`,
      {
        eventLabel: `Заявка на подовження терміну зберігання`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );

    const dialogRef = this.dialog.open(ChangeCustomerReturnComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          panelClass: 'no-padding',
          disableClose: true,
        }),
    });
    dialogRef.componentInstance.type = this.type;
    dialogRef.componentInstance.invoice = this.inputInvoice;
    dialogRef.componentInstance.user = this.corporate ? null : this.user;

    const keys$ = dialogRef
      .keydownEvents()
      .pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();

    merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.sis.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  checkIsCorporatePaymentDisabled() {
    return this.user.contacts && !this.paymentCostsInfo?.UseNovaPay;
  }

  checkPaymentDisabled(): boolean {
    if (this.inputInvoice?.isInternational) {
      return this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.PaymentNotAvailable;
    }

    return (
      !this.paymentCostsInfo ||
      (this.paymentCostsInfo && this.isPaid(this.paymentCostsInfo)) ||
      this.checkIsCorporatePaymentDisabled() ||
      this.paymentPopupOpened ||
      this.paymentCostsInfo.UseNovaPay === null
    );
  }

  showPaymentButtonForInternationalInvoice(): boolean {
    return this.inputInvoice?.isInternational && (
      this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.NeedPayment
      || this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.PaymentNotAvailable
    ) && !this.isPaid(this.paymentCostsInfo);
  }

  addDocumentToArchive(invoice: Invoice): void {
    const delay = () => timer(2000);
    this.invoicesService
      .addDocumentToArchive(invoice, this.type as 'in' | 'out')
      .pipe(delayWhen(delay))
      .subscribe(
        () => {
          this.alertToastService.pushSuccess(`Накладну успiшно заархiвовано`);
          this.sis.closeParams = { success: true };
          this.dialogRef.close();

          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.addDocumentToArchive`,
            {
              eventLabel: `Архівувати`,
              eventCategory: `${componentName}`,
              eventAction: 'click',
            },
          );
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track(
            'analitics',
            `${componentName}.addDocumentToArchive.error`,
            {
              eventLabel: `\`Помилка Заархiвувати: ${invoice.docNumber} ${
                errors[0] && errors[0].errorCode
              }`,
              eventCategory: `${componentName}`,
              eventAction: 'addDocumentToArchive.error',
            },
          );
        },
      );
  }

  goToNewInvoice(docNumber, event): void {
    event.preventDefault();
    this.showNewInvoice(docNumber);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.keyCode === 27) {
      this.dialog.closeAll();
    }
  }

  createPayment(invoice: Invoice, type: PayInvoiceType): void {
    this.paymentPopupOpened = true;
    const dialogRef = this.dialog.open(MasterpassPaymentComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          position: { right: '0' },
          width: '75%',
          height: '100%',
          panelClass: 'setting-content-popup',
        }),
    });
    dialogRef.componentInstance.type = type;
    dialogRef.componentInstance.inputInvoice = this.inputInvoice;
    dialogRef.componentInstance.invoiceData = {
      docNumber: this.inputInvoice.docNumber,
      SendersPhone: this.inputInvoice.PhoneSender ?? this.inputInvoice.SendersPhone,
      RecipientsPhone: (this.inputInvoice as any).PhoneRecipient ?? (this.inputInvoice as any).RecipientsPhone,
      Amount: this.inputInvoice.Cost,
    };
    dialogRef.componentInstance.paymentCostsInfo = this.paymentCostsInfo;
    dialogRef.componentInstance.recipientCounterpartyType =
      invoice.recipientCounterpartyType;

    dialogRef.afterClosed().subscribe(() => {
      this.paymentPopupOpened = false;
      this.getInvoice();
    });

    this.googleAnalyticsService.track(
      'analitics',
      `${componentName}.createPayment`,
      {
        eventLabel: `Сплатити`,
        eventCategory: `${componentName}`,
        eventAction: 'click',
      },
    );
  }

  isPaid(paymentCostsInfo: PaymentCostsInfo): boolean {
    if (!paymentCostsInfo) {
      return true;
    }
    if (
      paymentCostsInfo.Services > 0 ||
      paymentCostsInfo.AfterPayment > 0 ||
      paymentCostsInfo.MoneyTransfer > 0 ||
      paymentCostsInfo.InternationalDelivery > 0 ||
      paymentCostsInfo.Commission > 0 ||
      paymentCostsInfo.Redirecting > 0 ||
      paymentCostsInfo.Return > 0
    ) {
      return false;
    }

    return true;
  }

  showNewInvoice(documentNumber: string): void {
    this.setPending(true);
    this.newInvoicePending = true;

    this.subscriptions = this.getInvoiceByNumber(documentNumber)
      .pipe(finalize(() => {
        this.setPending(false);
        this.newInvoicePending = false;
      }))
      .subscribe((invoice: Invoice) => {
        this.closePopups();

        const invoiceToOpen = invoice ? invoice : this.newInvoice;
        const documentType = invoice
          ? (invoice.TypeOfDocument === 'Incoming' || invoice.TypeOfDocument === 'in') ? 'in' : 'out'
          : this.type;

        const isTrackedDocument = !invoice;

        const modalConfig = {
          ...(this.breakpointService.isMobile
            ? FullscreenMobileModalConfig
            : {
              panelClass: 'no-padding',
            }),
          disableClose: false,
        };

        let dialogRef: MatDialogRef<ShowInvoiceTrackedComponent | ShowInvoiceComponent>;
        if (isTrackedDocument) {
          dialogRef = this.dialog.open(ShowInvoiceTrackedComponent, modalConfig);
        } else {
          dialogRef = this.dialog.open(ShowInvoiceComponent, modalConfig);
          (dialogRef as MatDialogRef<ShowInvoiceComponent>).componentInstance.isInvoiceFromSearch = true;
        }

        dialogRef.componentInstance.type = documentType;
        dialogRef.componentInstance.inputInvoice = invoiceToOpen;
        dialogRef.backdropClick().subscribe(() => {
          this.dialogRef.close(this.sis.closeParams);
        });
      }, (errors) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
        }
      });
  }

  closePopups(): void {
    this.dialog.closeAll();
  }

  lightReturn(): void {
    this.lightReturnService.init(this.inputInvoice);
  }

  claim(invoice: Invoice): void {
    this.cs.open(invoice.docNumber);
  }

  private initInvoice(): void {
    this.backMoney = this.inputInvoice.backMoney;
    this.paymentControl = this.inputInvoice.paymentControl;
    this.corporate = !!this.user.contacts;
    this.currentStateIsArchived = this.router.url.includes(
      AppRoutes.invoices_archived,
    );
    this.paymentCalc();
    this.getInvoice();
    this.initTrackingDocuments();
  }

  private handleRedirectPopupClose(value: RedirectSuccessPopupData, type: string): Observable<{ success: boolean }> {
    if (!value) {
      return of({ success: false });
    }

    if (!isNumber(value?.Pricing) && !value.ScheduledDeliveryDate && value.success) {
      return of({ success: true });
    }

    const successDialog = this.dialog.open(RedirectSuccessPopupComponent, {
      data: {
        isCreated: value.isCreated,
        Number: value.Number,
        Invoice: this.inputInvoice,
        MessageText: value.MessageText,
        Type: type,
        Pricing: value.Pricing,
        ScheduledDeliveryDate: value.ScheduledDeliveryDate,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleReturnPopupClose(value: BackSuccessPopupData, invoice: Invoice, type: string): Observable<{
    success: boolean
  }> {
    if (!value) {
      return of({ success: false });
    }

    if (!isNumber(value?.Pricing) && !value.ScheduledDeliveryDate && value.success) {
      return of({ success: true });
    }

    const successDialog = this.dialog.open(BackSuccessPopupComponent, {
      data: {
        isCreated: value.isCreated,
        Number: value.Number,
        Pricing: value.Pricing,
        ScheduledDeliveryDate: value.ScheduledDeliveryDate,
        Invoice: invoice,
        Type: type,
        MessageText: value.MessageText,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleChangeEwPopupClose(value: ChangeEwSuccessPopupData): Observable<{ success: boolean }> {
    if (!value) {
      return of({ success: false });
    }

    const successDialog = this.dialog.open(ChangeEwSuccessPopupComponent, {
      data: {
        AlertForCall: value.AlertForCall,
        MessageText: value.MessageText,
        Number: value.Number,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleInvoiceMutations(invoice: Invoice): void {
    this.invoice = handleFirstDayStorage(invoice);
    this.invoice = handleReturnCargoDate(this.invoice);
  }

  private initTrackingDocuments(): void {
    if (this.storage.isStorageAvailable()) {
      const trackingDocumentsArray =
        this.trackDeliveryService.getTrackingDocuments();
      if (!!trackingDocumentsArray) {
        this.trackingDocuments = trackingDocumentsArray;
      }
    }
  }

  private setPending(value: boolean): void {
    this.pending.next(value);
  }

  private getInvoice(): void {
    this.formattedInvoiceNumber = this.invoiceService.makeFormatedInvoiceNumber(
      this.inputInvoice.docNumber,
    );
    this.setPending(true);
    this.invoiceService
      .track(
        this.inputInvoice.docNumber,
        this.inputInvoice?.PhoneSender ?? undefined,
      )
      .pipe(
        switchMap((invoice) => {
          this.getStatusDocumentsInvoice = invoice;

          invoice.announcedPrice = this.inputInvoice?.announcedPrice || this.inputInvoice?.Cost;
          invoice.RecipientFullName = this.inputInvoice?.RecipientFullName;

          this.patchTableInvoiceWithTracked(invoice);

          this.allowManageButton = AllowedStatusCodes.some(
            (e) => invoice.statusCode === e,
          );

          this.handleInvoiceMutations(this.isInvoiceFromSearch ? this.inputInvoice : invoice);
          this.invoice.CheckWeightMethod = invoice.CheckWeightMethod;
          this.inputInvoice.state = this.invoice.state;

          const isTodayGreaterThanFirstDayStorage = (i: Invoice): boolean => {
            if (!i) {
              return false;
            }
            try {
              const dateFirstDayStorage = new Date(i.dateFirstDayStorage);
              return todayEqualOrGreaterThan(dateFirstDayStorage);
            } catch (e) {
              return false;
            }
          };

          // @TODO this logic should be revised by business analyst
          if (
            this.isInvoiceFromSearch &&
            ((!([7, 8].some(statusCode => statusCode === Number(invoice.statusCode))) &&
            !(
              invoice &&
              invoice.statusCode &&
              !!invoice.dateFirstDayStorage &&
              isTodayGreaterThanFirstDayStorage(invoice)
            ))
            || ([105].some(statusCode => statusCode === Number(invoice.statusCode))))
          ) {
            this.invoice.state.name = invoice.state.name;
          }

          if (this.invoice.serviceRequests) {
            this.invoice.serviceRequests.forEach((request) => {
              if (request.serviceName === 'Redirecting') {
                this.redirectRequest = request;
              }

              if (request.serviceName === 'CargoReturn') {
                this.returnRequest = request;
              }
            });
          }

          if (this.redirectRequest || this.returnRequest) {
            this.invoiceService
              .track(
                (this.redirectRequest || this.returnRequest).newInvoiceNumber,
              )
              .pipe(take(1))
              .subscribe((i) => {
                this.newInvoice = i;
              });
          }

          return this.masterpassService.getPaymentInfo(
            this.invoice.docNumber,
            this.type === 'in'
              ? this.invoice.recipient &&
              (this.invoice.recipient.phone as string)
              : this.authService.user.phone ||
              (this.invoice.sender ? this.invoice.sender.phone : ''),
          );
        }),
        finalize(() => this.setPending(false)),
        take(1),
      )
      .subscribe((paymentCostsInfo) => {
        this.paymentCostsInfo = paymentCostsInfo;
      });
  }

  private patchTableInvoiceWithTracked(invoice: Invoice): void {
    this.inputInvoice.statusCode = invoice.statusCode;
    this.inputInvoice.actualDeliveryDate = invoice.actualDeliveryDate;
    this.inputInvoice.dateReturnCargo = invoice.dateReturnCargo;
    this.inputInvoice.dateFirstDayStorage = invoice.dateFirstDayStorage;
    this.inputInvoice.serviceRequests = invoice.serviceRequests;
    this.inputInvoice.sender = invoice.sender;
    this.inputInvoice.recipient = invoice.recipient;
    this.inputInvoice.authenticData = invoice.authenticData;
  }

  private trackAnalytics(name: AnalyticEventName): void {
    this.analyticsService.track({ name });
  }

  private getInvoiceByNumber(invoiceNumber: string): Observable<Invoice | null> {
    return this.invoicesService.searchInvoices(invoiceNumber, { page: 1, limit: 5 })
      .pipe(finalize(() => this.setPending(false)),
        map(({ invoices, totalCount }) => {
          if (!totalCount) {
            return null;
          }

          return invoices.map(innerInvoices => innerInvoices.map(handleFirstDayStorage)[0])[0];
        }),
      );
  }
}
